import React, { useState, useEffect } from 'react';
import { ChevronLeft, Download, ArrowRight, Zap, BarChart3, DollarSign, Target, RotateCcw, HelpCircle } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// BRAND COLORS (from branding document)
// ═══════════════════════════════════════════════════════════════
const COLORS = {
  bg: '#0A0B1E',
  bg2: '#1A1133',
  primary: '#4E62F4',
  primaryLight: '#637CE2',
  lime: '#C6FD38',
  pink: '#C28CFA',
  lavender: '#95A0F0',
  success: '#5AE5A4',
  warn: '#FFB84D',
  danger: '#FF6B6B',
  text: '#FFFFFF',
  text2: '#B8B5D9',
  muted: '#6E6B89',
};

// ═══════════════════════════════════════════════════════════════
// CATEGORIES (without emojis for PDF compatibility)
// ═══════════════════════════════════════════════════════════════
const CATEGORIES = {
  captacion: { name: 'Captación', color: '#4E62F4', icon: '1' },
  conversion: { name: 'Conversión', color: '#C28CFA', icon: '2' },
  oferta: { name: 'Oferta y pricing', color: '#C6FD38', icon: '3' },
  retencion: { name: 'Retención y recurrencia', color: '#95A0F0', icon: '4' },
  automatizacion: { name: 'Automatización', color: '#637CE2', icon: '5' },
  metricas: { name: 'Métricas y datos', color: '#5AE5A4', icon: '6' }
};

// Category display icons (emojis for UI only)
const CATEGORY_EMOJIS = {
  captacion: '🧲',
  conversion: '🎯',
  oferta: '💎',
  retencion: '🔁',
  automatizacion: '⚙️',
  metricas: '📈'
};

// ═══════════════════════════════════════════════════════════════
// GLOSSARY - Marketing terms explanations
// ═══════════════════════════════════════════════════════════════
const GLOSSARY = {
  'lead': 'Un lead es una persona que ha mostrado interés en tu producto o servicio (por ejemplo, dejando su email).',
  'lead magnet': 'Un lead magnet es un recurso gratuito (ebook, checklist, webinar) que ofreces a cambio del email del visitante.',
  'CPL': 'CPL (Coste Por Lead) es cuánto dinero te cuesta conseguir cada persona interesada en tu negocio.',
  'tasa de conversión': 'La tasa de conversión es el porcentaje de personas que realizan la acción deseada (comprar, registrarse, etc.).',
  'secuencia de venta': 'Una secuencia de venta es una serie de mensajes automáticos (emails, WhatsApp) diseñados para convertir leads en clientes.',
  'promesa': 'Tu promesa es el beneficio principal que ofreces y que te diferencia de la competencia.',
  'escalera de valor': 'Una escalera de valor son diferentes productos a distintos precios para captar clientes con cualquier presupuesto.',
  'ingresos recurrentes': 'Ingresos recurrentes son los que se repiten cada mes (suscripciones, membresías, contratos renovables).',
  'upsell': 'Upsell es ofrecer un producto de mayor valor justo después de una compra.',
  'cross-sell': 'Cross-sell es ofrecer productos complementarios al que el cliente ya compró.',
  'automatización': 'Automatización es configurar procesos que funcionan solos sin tu intervención manual.',
  'CAC': 'CAC (Coste de Adquisición de Cliente) es cuánto te cuesta conseguir un nuevo cliente.',
  'LTV': 'LTV (Lifetime Value) es el valor total que un cliente te genera durante toda su relación contigo.',
  'ROAS': 'ROAS (Return On Ad Spend) es cuánto dinero ganas por cada euro invertido en publicidad.'
};

// ═══════════════════════════════════════════════════════════════
// QUESTIONS WITH EXPLANATIONS
// ═══════════════════════════════════════════════════════════════
const REVENUE_QUESTION = {
  id: 'facturacion',
  cat: '_data',
  title: '¿Cuánto factura tu negocio al mes?',
  sub: 'Necesario para calcular cuánto dinero estás dejando sobre la mesa.',
  glossaryTerms: [],
  options: [
    { label: 'Menos de 1.000 € / aún no facturo', val: 500 },
    { label: '1.000 € - 3.000 €', val: 2000 },
    { label: '3.000 € - 8.000 €', val: 5500 },
    { label: '8.000 € - 20.000 €', val: 14000 },
    { label: 'Más de 20.000 €', val: 30000 }
  ]
};

const QUESTIONS = [
  { 
    id: 'q1', cat: 'captacion', 
    title: '¿Tienes un sistema de captación de leads activo y constante?', 
    sub: 'Por ejemplo: un lead magnet, anuncios o contenido que trae personas nuevas cada día.',
    glossaryTerms: ['lead', 'lead magnet'],
    options: [
      { label: 'Sí, capto leads nuevos todos los días de forma automática', score: 10 }, 
      { label: 'Tengo algo, pero es inconsistente', score: 5 }, 
      { label: 'Capto solo cuando hago algo puntual', score: 2 }, 
      { label: 'No tengo sistema de captación', score: 0 }
    ] 
  },
  { 
    id: 'q2', cat: 'captacion', 
    title: '¿Sabes cuánto te cuesta conseguir un lead (CPL)?', 
    sub: 'El coste medio de captar a una persona interesada en tu negocio.',
    glossaryTerms: ['CPL', 'lead'],
    options: [
      { label: 'Sí, lo mido y lo optimizo constantemente', score: 10 }, 
      { label: 'Lo sé aproximadamente', score: 6 }, 
      { label: 'No tengo ni idea', score: 2 }, 
      { label: 'No invierto en captación', score: 0 }
    ] 
  },
  { 
    id: 'q3', cat: 'conversion', 
    title: '¿Conoces tu tasa de conversión de lead a cliente?', 
    sub: 'Qué porcentaje de tus leads acaban comprando.',
    glossaryTerms: ['tasa de conversión', 'lead'],
    options: [
      { label: 'Sí, la mido por canal y la optimizo', score: 10 }, 
      { label: 'La conozco a grandes rasgos', score: 6 }, 
      { label: 'No la mido', score: 2 }, 
      { label: 'No tengo proceso de venta definido', score: 0 }
    ] 
  },
  { 
    id: 'q4', cat: 'conversion', 
    title: '¿Tienes una secuencia de venta automatizada?', 
    sub: 'Emails o WhatsApp que venden por ti tras captar el lead.',
    glossaryTerms: ['secuencia de venta', 'lead'],
    options: [
      { label: 'Sí, completa (emails + WhatsApp) y optimizada', score: 10 }, 
      { label: 'Tengo emails básicos', score: 5 }, 
      { label: 'Solo vendo manualmente / en directo', score: 2 }, 
      { label: 'No tengo nada', score: 0 }
    ] 
  },
  { 
    id: 'q5', cat: 'oferta', 
    title: '¿Tu oferta tiene una promesa clara y diferenciada?', 
    sub: '¿Por qué te comprarían a ti y no a la competencia?',
    glossaryTerms: ['promesa'],
    options: [
      { label: 'Sí, mi promesa es única y la comunico bien', score: 10 }, 
      { label: 'Tengo una promesa, pero no destaca', score: 5 }, 
      { label: 'Vendo lo mismo que todos', score: 2 }, 
      { label: 'No tengo una promesa clara', score: 0 }
    ] 
  },
  { 
    id: 'q6', cat: 'oferta', 
    title: '¿Tienes varios niveles de precio (entrada, core, premium)?', 
    sub: 'Una escalera de valor para captar clientes con cualquier presupuesto.',
    glossaryTerms: ['escalera de valor'],
    options: [
      { label: 'Sí, tengo una escalera de productos completa', score: 10 }, 
      { label: 'Tengo 2 niveles', score: 6 }, 
      { label: 'Solo tengo un producto/precio', score: 3 }, 
      { label: 'No tengo estructura de precios pensada', score: 0 }
    ] 
  },
  { 
    id: 'q7', cat: 'retencion', 
    title: '¿Tienes ingresos recurrentes o solo ventas únicas?', 
    sub: 'Membresías, suscripciones o contratos que se renuevan automáticamente.',
    glossaryTerms: ['ingresos recurrentes'],
    options: [
      { label: 'Sí, gran parte de mis ingresos son recurrentes', score: 10 }, 
      { label: 'Algo recurrente, pero poco', score: 5 }, 
      { label: 'Casi todo son ventas únicas', score: 2 }, 
      { label: 'Solo ventas únicas', score: 0 }
    ] 
  },
  { 
    id: 'q8', cat: 'retencion', 
    title: '¿Tienes sistema de upsell / cross-sell post-compra?', 
    sub: 'Ofertas adicionales tras la primera compra para aumentar el valor medio por cliente.',
    glossaryTerms: ['upsell', 'cross-sell'],
    options: [
      { label: 'Sí, automatizado y funcionando', score: 10 }, 
      { label: 'Lo hago a veces, manualmente', score: 5 }, 
      { label: 'No vendo nada más tras la compra', score: 2 }, 
      { label: 'Ni me lo había planteado', score: 0 }
    ] 
  },
  { 
    id: 'q9', cat: 'automatizacion', 
    title: '¿Qué parte de tu marketing está automatizado?', 
    sub: 'Procesos que funcionan sin tu intervención manual.',
    glossaryTerms: ['automatización'],
    options: [
      { label: 'La mayoría: capto, nutro y vendo en automático', score: 10 }, 
      { label: 'Algunas cosas automatizadas', score: 6 }, 
      { label: 'Casi todo lo hago a mano', score: 2 }, 
      { label: 'Todo es 100% manual', score: 0 }
    ] 
  },
  { 
    id: 'q10', cat: 'automatizacion', 
    title: '¿Tu negocio depende de tu tiempo personal para vender?', 
    sub: '¿Vendes solo si estás tú presente?',
    glossaryTerms: [],
    options: [
      { label: 'No, vende sin mí (sistemas + automatización)', score: 10 }, 
      { label: 'Parcialmente, algunas cosas van solas', score: 5 }, 
      { label: 'Sí, si no estoy yo no se vende', score: 2 }, 
      { label: 'Soy yo el cuello de botella de todo', score: 0 }
    ] 
  },
  { 
    id: 'q11', cat: 'metricas', 
    title: '¿Mides tus números clave (CAC, LTV, ROAS)?', 
    sub: 'Métricas esenciales para tomar decisiones basadas en datos.',
    glossaryTerms: ['CAC', 'LTV', 'ROAS'],
    options: [
      { label: 'Sí, los mido y decido con datos', score: 10 }, 
      { label: 'Mido algunos', score: 5 }, 
      { label: 'No mido casi nada', score: 2 }, 
      { label: 'No sé qué son', score: 0 }
    ] 
  },
  { 
    id: 'q12', cat: 'metricas', 
    title: '¿Sabes qué canal te trae los mejores clientes?', 
    sub: 'No solo más leads, sino los que más compran y mejor.',
    glossaryTerms: ['lead'],
    options: [
      { label: 'Sí, sé exactamente de dónde viene mi mejor cliente', score: 10 }, 
      { label: 'Tengo una idea aproximada', score: 6 }, 
      { label: 'No lo tengo claro', score: 2 }, 
      { label: 'No hago seguimiento de origen', score: 0 }
    ] 
  }
];

const GAP_FIXES = {
  captacion: 'Monta un sistema de captación constante (lead magnet + ads/contenido) que traiga leads cada día.',
  conversion: 'Implementa una secuencia de venta automatizada por email + WhatsApp y mide tu conversión por canal.',
  oferta: 'Clarifica tu promesa diferencial y crea una escalera de precios (entrada, core, premium).',
  retencion: 'Añade ingresos recurrentes y un sistema de upsell post-compra para multiplicar el valor por cliente.',
  automatizacion: 'Automatiza captación, nurturing y venta para dejar de ser el cuello de botella de tu negocio.',
  metricas: 'Empieza a medir CAC, LTV y ROAS y a rastrear el origen de tus mejores clientes.'
};

const BENCHMARK = { media: 54, top10: 84 };
const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_copy-design-ai/artifacts/9wth0yph_LOGO%20PARA%20FONDO%20OSCURO.png';
const LOGO_LOCAL = '/logo-fascinai.png'; // Local copy for PDF

// ═══════════════════════════════════════════════════════════════
// CONFETTI COMPONENT
// ═══════════════════════════════════════════════════════════════
const Confetti = ({ active }) => {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    if (active) {
      const newParticles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 2,
        color: [COLORS.lime, COLORS.pink, COLORS.lavender, COLORS.primary][Math.floor(Math.random() * 4)],
        size: 6 + Math.random() * 8,
        rotation: Math.random() * 360
      }));
      setParticles(newParticles);
      
      const timer = setTimeout(() => setParticles([]), 4000);
      return () => clearTimeout(timer);
    }
  }, [active]);

  if (!active || particles.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute animate-confetti"
          style={{
            left: `${p.x}%`,
            top: '-20px',
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            transform: `rotate(${p.rotation}deg)`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ANIMATED LOADER
// ═══════════════════════════════════════════════════════════════
const AnimatedLoader = () => (
  <div className="relative w-24 h-24 mx-auto mb-8">
    <div className="absolute inset-0 rounded-full border-2 border-white/10" />
    <svg className="absolute inset-0 w-full h-full animate-spin" style={{ animationDuration: '2s' }}>
      <defs>
        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={COLORS.lime} />
          <stop offset="50%" stopColor={COLORS.primary} />
          <stop offset="100%" stopColor={COLORS.pink} />
        </linearGradient>
      </defs>
      <circle
        cx="48"
        cy="48"
        r="44"
        fill="none"
        stroke="url(#gradient)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="180 270"
      />
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <BarChart3 className="w-10 h-10 text-white/80 animate-pulse" />
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// GLASSMORPHISM CARD
// ═══════════════════════════════════════════════════════════════
const GlassCard = ({ children, className = '', highlight = false }) => (
  <div 
    className={`
      relative overflow-hidden rounded-3xl p-6 md:p-8
      backdrop-blur-xl border transition-all duration-500
      ${highlight 
        ? 'bg-gradient-to-br from-white/[0.08] to-white/[0.02] border-white/20 shadow-lg shadow-[#4E62F4]/10' 
        : 'bg-white/[0.04] border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.06]'
      }
      ${className}
    `}
  >
    {highlight && (
      <div className="absolute inset-0 bg-gradient-to-br from-[#4E62F4]/5 via-transparent to-[#C28CFA]/5 pointer-events-none" />
    )}
    <div className="relative z-10">{children}</div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// GLOSSARY TOOLTIP
// ═══════════════════════════════════════════════════════════════
const GlossaryTooltip = ({ terms }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!terms || terms.length === 0) return null;
  
  return (
    <div className="mt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-white/40 hover:text-white/60 transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
        <span>{expanded ? 'Ocultar explicaciones' : '¿No entiendes algún término? Click aquí'}</span>
      </button>
      
      {expanded && (
        <div className="mt-3 p-4 rounded-xl bg-white/[0.03] border border-white/[0.08] space-y-2">
          {terms.map(term => (
            <div key={term} className="text-sm">
              <span className="text-[#C6FD38] font-medium">{term}:</span>{' '}
              <span className="text-white/50">{GLOSSARY[term]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const AuditorMarketing = () => {
  const [screen, setScreen] = useState('intro');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revenue, setRevenue] = useState(0);
  const [result, setResult] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [pendingResult, setPendingResult] = useState(null);

  // Load GHL form script
  useEffect(() => {
    if (screen === 'leadCapture') {
      const existingScript = document.querySelector('script[src="https://link.msgsndr.com/js/form_embed.js"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://link.msgsndr.com/js/form_embed.js';
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, [screen]);

  const allQuestions = [REVENUE_QUESTION, ...QUESTIONS];
  const totalQuestions = allQuestions.length;
  const progress = ((questionIndex + 1) / totalQuestions) * 100;

  const calcCategoryScores = () => {
    const totals = {};
    const counts = {};
    const scores = {};
    
    QUESTIONS.forEach(q => {
      const answer = answers[q.id];
      if (answer !== undefined) {
        totals[q.cat] = (totals[q.cat] || 0) + answer;
        counts[q.cat] = (counts[q.cat] || 0) + 1;
      }
    });
    
    Object.keys(CATEGORIES).forEach(cat => {
      scores[cat] = counts[cat] ? Math.round((totals[cat] / (counts[cat] * 10)) * 100) : 0;
    });
    
    return scores;
  };

  const calcGlobalScore = (categoryScores) => {
    const values = Object.values(categoryScores);
    return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  };

  const calcMoneyLost = (globalScore) => {
    const factor = Math.min(((100 - globalScore) / 100) * 1.25, 1.3);
    return Math.round(revenue * factor);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return COLORS.success;
    if (score >= 40) return COLORS.warn;
    return COLORS.danger;
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return 'Sólido - listo para escalar';
    if (score >= 40) return 'Regular - con huecos importantes';
    return 'Crítico - pierdes mucho dinero';
  };

  const formatNumber = (n) => n.toLocaleString('es-ES');

  const handleStart = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setScreen('questions');
      setIsAnimating(false);
    }, 300);
  };

  const handleAnswer = (optionIndex) => {
    const currentQ = allQuestions[questionIndex];
    const option = currentQ.options[optionIndex];
    
    setSelectedOption(optionIndex);
    
    setTimeout(() => {
      if (currentQ.cat === '_data') {
        setRevenue(option.val);
        setAnswers(prev => ({ ...prev, [currentQ.id]: option.val }));
      } else {
        setAnswers(prev => ({ ...prev, [currentQ.id]: option.score }));
      }

      setIsAnimating(true);
      
      setTimeout(() => {
        setSelectedOption(null);
        if (questionIndex < totalQuestions - 1) {
          setQuestionIndex(prev => prev + 1);
        } else {
          showResults();
        }
        setIsAnimating(false);
      }, 300);
    }, 200);
  };

  const showResults = () => {
    const categoryScores = calcCategoryScores();
    const globalScore = calcGlobalScore(categoryScores);
    const moneyLost = calcMoneyLost(globalScore);
    const orderedCats = Object.keys(categoryScores).sort((a, b) => categoryScores[a] - categoryScores[b]);
    const gaps = orderedCats.filter(k => categoryScores[k] < 100).slice(0, 3);

    // Store pending result and show lead capture
    setPendingResult({ globalScore, categoryScores, moneyLost, gaps });
    setScreen('leadCapture');
  };

  const handleShowFinalResult = () => {
    setScreen('loading');
    setTimeout(() => {
      setResult(pendingResult);
      setScreen('result');
      setTimeout(() => setShowConfetti(true), 300);
    }, 1500);
  };

  const handleBack = () => {
    if (questionIndex > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setQuestionIndex(prev => prev - 1);
        setIsAnimating(false);
      }, 200);
    }
  };

  const handleRestart = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setScreen('intro');
      setQuestionIndex(0);
      setAnswers({});
      setRevenue(0);
      setResult(null);
      setPendingResult(null);
      setShowConfetti(false);
      setIsAnimating(false);
    }, 300);
  };

  const handleDownloadPDF = async () => {
    if (!result) return;
    
    const jsPDFModule = await import('jspdf');
    const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    let y = 40;

    // Background
    doc.setFillColor(10, 11, 30);
    doc.rect(0, 0, W, H, 'F');
    
    // Top gradient bar
    doc.setFillColor(78, 98, 244);
    doc.rect(0, 0, W, 6, 'F');

    // Load and add logo image
    try {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        logoImg.src = LOGO_LOCAL;
      });
      
      // Create canvas to get image data
      const canvas = document.createElement('canvas');
      canvas.width = logoImg.width;
      canvas.height = logoImg.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(logoImg, 0, 0);
      const logoData = canvas.toDataURL('image/png');
      
      // Add logo centered (width ~200px, maintain aspect ratio)
      const logoWidth = 200;
      const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
      doc.addImage(logoData, 'PNG', (W - logoWidth) / 2, y, logoWidth, logoHeight);
      y += logoHeight + 15;
    } catch (e) {
      // Fallback to text if image fails
      if (process.env.NODE_ENV === 'development') console.log('Logo load failed, using text fallback', e);
      doc.setTextColor(198, 253, 56);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(28);
      doc.text('FASCINAI ONE', W / 2, y + 10, { align: 'center' });
      y += 30;
    }
    
    doc.setFontSize(11);
    doc.setTextColor(184, 181, 217);
    doc.setFont('helvetica', 'normal');
    doc.text('Tu Auditoria de Marketing Personalizada', W / 2, y, { align: 'center' });
    y += 40;

    // Score section
    doc.setFillColor(255, 255, 255, 0.03);
    doc.roundedRect(50, y - 15, W - 100, 110, 12, 12, 'F');
    doc.setDrawColor(255, 255, 255, 0.1);
    doc.roundedRect(50, y - 15, W - 100, 110, 12, 12, 'S');
    
    const scoreColor = result.globalScore >= 70 ? [90, 229, 164] : result.globalScore >= 40 ? [255, 184, 77] : [255, 107, 107];
    doc.setTextColor(...scoreColor);
    doc.setFontSize(72);
    doc.setFont('helvetica', 'bold');
    doc.text(String(result.globalScore), W / 2 - 25, y + 50, { align: 'center' });
    
    doc.setFontSize(28);
    doc.setTextColor(110, 107, 137);
    doc.text('/100', W / 2 + 50, y + 50);
    
    doc.setFontSize(14);
    doc.setTextColor(...scoreColor);
    doc.text(getScoreLabel(result.globalScore), W / 2, y + 85, { align: 'center' });
    y += 130;

    // Money lost section
    doc.setFillColor(255, 107, 107, 0.08);
    doc.roundedRect(50, y, W - 100, 85, 12, 12, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(184, 181, 217);
    doc.text('DINERO QUE ESTAS DEJANDO SOBRE LA MESA', W / 2, y + 22, { align: 'center' });
    
    doc.setFontSize(36);
    doc.setTextColor(255, 107, 107);
    doc.setFont('helvetica', 'bold');
    doc.text(formatNumber(result.moneyLost) + ' EUR / mes', W / 2, y + 55, { align: 'center' });
    
    doc.setFontSize(11);
    doc.setTextColor(184, 181, 217);
    doc.setFont('helvetica', 'normal');
    doc.text('(' + formatNumber(result.moneyLost * 12) + ' EUR al ano)', W / 2, y + 75, { align: 'center' });
    y += 105;

    // Category scores header
    doc.setFontSize(11);
    doc.setTextColor(198, 253, 56);
    doc.setFont('helvetica', 'bold');
    doc.text('TU SCORE POR AREA', 50, y);
    y += 20;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    // Category scores with proper spacing
    Object.keys(CATEGORIES).forEach(cat => {
      const score = result.categoryScores[cat];
      const color = score >= 70 ? [90, 229, 164] : score >= 40 ? [255, 184, 77] : [255, 107, 107];
      
      // Category name
      doc.setTextColor(255, 255, 255);
      doc.text(CATEGORIES[cat].name, 50, y);
      
      // Progress bar background - positioned after text
      const barX = 180;
      const barWidth = 280;
      doc.setFillColor(255, 255, 255, 0.08);
      doc.roundedRect(barX, y - 9, barWidth, 12, 4, 4, 'F');
      
      // Progress bar fill
      doc.setFillColor(...color);
      const fillWidth = Math.max((score / 100) * barWidth, 4);
      doc.roundedRect(barX, y - 9, fillWidth, 12, 4, 4, 'F');
      
      // Score text
      doc.setTextColor(...color);
      doc.text(`${score}/100`, W - 50, y, { align: 'right' });
      y += 24;
    });

    y += 15;

    // Gaps section
    if (result.gaps.length > 0) {
      doc.setFontSize(11);
      doc.setTextColor(255, 107, 107);
      doc.setFont('helvetica', 'bold');
      doc.text('TUS HUECOS CRITICOS', 50, y);
      y += 20;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(184, 181, 217);

      result.gaps.forEach((cat, idx) => {
        const text = `${idx + 1}. ${CATEGORIES[cat].name}: ${GAP_FIXES[cat]}`;
        const lines = doc.splitTextToSize(text, W - 100);
        doc.text(lines, 50, y);
        y += lines.length * 13 + 8;
      });
    }

    y += 15;

    // Action plan
    doc.setFillColor(90, 229, 164, 0.08);
    doc.roundedRect(50, y, W - 100, 60, 12, 12, 'F');
    
    doc.setFontSize(12);
    doc.setTextColor(90, 229, 164);
    doc.setFont('helvetica', 'bold');
    doc.text('TU PLAN DE ACCION', W / 2, y + 22, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal');
    doc.text('Trabaja en tus huecos criticos para subir 30 puntos en 30 dias.', W / 2, y + 42, { align: 'center' });

    // Footer
    doc.setFontSize(9);
    doc.setTextColor(110, 107, 137);
    doc.text('FASCINAI ONE - Tu marketing completo con IA', W / 2, H - 30, { align: 'center' });

    doc.save('Auditoria-Marketing-FASCINAI.pdf');
  };

  // ═══════════════════════════════════════════════════════════════
  // BACKGROUND
  // ═══════════════════════════════════════════════════════════════
  const Background = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div 
        className="absolute inset-0"
        style={{ 
          background: `
            radial-gradient(ellipse 80% 50% at 20% -10%, ${COLORS.primary}30 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 80% 10%, ${COLORS.pink}20 0%, transparent 50%),
            radial-gradient(ellipse 50% 30% at 50% 100%, ${COLORS.lavender}15 0%, transparent 50%),
            linear-gradient(180deg, ${COLORS.bg} 0%, ${COLORS.bg2} 100%)
          `
        }}
      />
      <div 
        className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl animate-pulse"
        style={{ 
          background: `radial-gradient(circle, ${COLORS.lime}40, transparent 70%)`,
          top: '10%',
          right: '-10%',
          animationDuration: '4s'
        }}
      />
      <div 
        className="absolute w-72 h-72 rounded-full opacity-15 blur-3xl animate-pulse"
        style={{ 
          background: `radial-gradient(circle, ${COLORS.pink}50, transparent 70%)`,
          bottom: '20%',
          left: '-5%',
          animationDuration: '5s',
          animationDelay: '1s'
        }}
      />
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // INTRO SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (screen === 'intro') {
    return (
      <div className="min-h-screen text-white relative">
        <Background />
        <Confetti active={false} />
        
        <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0A0B1E]/60 border-b border-white/5">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-center">
            <img src={LOGO_DARK} alt="FASCINAI ONE" className="h-9 md:h-10 object-contain" />
          </div>
        </header>

        <main className="relative z-10 pt-28 pb-20 px-4">
          <div className={`max-w-xl mx-auto text-center transition-all duration-500 ${isAnimating ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'}`}>
            {/* Badge */}
            <div 
              className="inline-block px-5 py-2.5 rounded-full text-xs font-bold tracking-wider uppercase mb-10"
              style={{ 
                background: `linear-gradient(135deg, ${COLORS.lime}20, ${COLORS.primary}20)`,
                border: `1px solid ${COLORS.lime}40`,
                color: COLORS.lime
              }}
            >
              Auditoría Gratuita · 3 min
            </div>

            {/* Icon - Fixed visibility */}
            <div 
              className="w-28 h-28 mx-auto mb-10 rounded-3xl flex items-center justify-center relative overflow-hidden"
              style={{ 
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.pink})`,
                boxShadow: `0 20px 60px ${COLORS.primary}50`
              }}
            >
              <BarChart3 className="w-14 h-14 text-white relative z-10" strokeWidth={1.5} />
            </div>

            {/* Headline */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Descubre cuánto dinero estás{' '}
              <span style={{ color: COLORS.lime }}>dejando sobre la mesa</span>{' '}
              cada mes
            </h1>

            {/* Subheadline */}
            <p className="text-base md:text-lg text-white/50 mb-12 max-w-lg mx-auto leading-relaxed">
              Responde 13 preguntas sobre tu marketing y tu negocio. En 3 minutos tendrás un diagnóstico completo con tu score, los huecos que te están costando dinero y un plan para recuperarlo.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mb-12">
              {[
                { icon: <BarChart3 className="w-4 h-4" />, text: 'Score 0-100', color: COLORS.primary },
                { icon: <DollarSign className="w-4 h-4" />, text: 'Dinero perdido/mes', color: COLORS.danger },
                { icon: <Target className="w-4 h-4" />, text: '3 huecos críticos', color: COLORS.pink },
                { icon: <Zap className="w-4 h-4" />, text: 'Plan de acción', color: COLORS.lime }
              ].map((feat) => (
                <GlassCard key={feat.text} className="!p-4 !rounded-2xl">
                  <div className="flex items-center justify-center gap-2 text-white/70 text-sm">
                    <span style={{ color: feat.color }}>{feat.icon}</span>
                    <span>{feat.text}</span>
                  </div>
                </GlassCard>
              ))}
            </div>

            {/* CTA Button */}
            <button
              onClick={handleStart}
              data-testid="start-audit-btn"
              className="group inline-flex items-center gap-3 px-10 py-5 text-lg font-bold rounded-2xl transition-all duration-300 hover:scale-105 active:scale-95"
              style={{ 
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.pink})`,
                boxShadow: `0 10px 40px ${COLORS.primary}40`,
                color: '#fff'
              }}
            >
              Empezar mi auditoría
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </main>

        <footer className="relative z-10 border-t border-white/5 py-6">
          <div className="max-w-4xl mx-auto px-4 text-center text-white/30 text-sm">
            <p>© 2024 FASCINAI ONE. Todos los derechos reservados.</p>
          </div>
        </footer>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // QUESTIONS SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (screen === 'questions') {
    const currentQ = allQuestions[questionIndex];
    const category = currentQ.cat !== '_data' ? CATEGORIES[currentQ.cat] : null;
    const categoryEmoji = currentQ.cat !== '_data' ? CATEGORY_EMOJIS[currentQ.cat] : null;

    return (
      <div className="min-h-screen text-white relative">
        <Background />
        
        <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0A0B1E]/60 border-b border-white/5">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <img src={LOGO_DARK} alt="FASCINAI ONE" className="h-7 md:h-8 object-contain" />
            <span className="text-sm text-white/40 font-medium">
              {questionIndex + 1} / {totalQuestions}
            </span>
          </div>
        </header>

        <main className="relative z-10 pt-24 pb-16 px-4">
          <div className="max-w-xl mx-auto">
            {/* Progress Bar */}
            <div className="mb-10">
              <div className="h-2 bg-white/5 rounded-full overflow-hidden backdrop-blur-sm">
                <div 
                  className="h-full rounded-full transition-all duration-700 ease-out relative overflow-hidden"
                  style={{ 
                    width: `${progress}%`,
                    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.lime})`
                  }}
                >
                  <div 
                    className="absolute inset-0 w-full"
                    style={{
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                      animation: 'shimmer 2s infinite'
                    }}
                  />
                </div>
              </div>
              <style>{`
                @keyframes shimmer {
                  0% { transform: translateX(-100%); }
                  100% { transform: translateX(100%); }
                }
              `}</style>
            </div>

            {/* Back Button */}
            {questionIndex > 0 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-white/40 hover:text-white/70 text-sm mb-6 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Volver
              </button>
            )}

            {/* Question Content */}
            <div className={`transition-all duration-400 ${isAnimating ? 'opacity-0 translate-x-12' : 'opacity-100 translate-x-0'}`}>
              {/* Category Tag */}
              {category && (
                <div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold tracking-wide uppercase mb-6 backdrop-blur-sm"
                  style={{ 
                    background: `${category.color}15`,
                    border: `1px solid ${category.color}30`,
                    color: category.color
                  }}
                >
                  <span>{categoryEmoji}</span>
                  <span>{category.name}</span>
                </div>
              )}

              {/* Question Title */}
              <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">
                {currentQ.title}
              </h2>

              {/* Question Subtitle */}
              <p className="text-white/40 mb-6 text-base leading-relaxed">
                {currentQ.sub}
              </p>

              {/* Glossary Tooltip */}
              <GlossaryTooltip terms={currentQ.glossaryTerms} />

              {/* Options */}
              <div className="space-y-3 mt-6">
                {currentQ.options.map((option, idx) => (
                  <button
                    key={option.text || `option-${idx}`}
                    onClick={() => handleAnswer(idx)}
                    data-testid={`option-${idx}`}
                    disabled={selectedOption !== null}
                    className={`
                      w-full flex items-center gap-4 p-5 rounded-2xl backdrop-blur-sm
                      border transition-all duration-300 text-left group
                      ${selectedOption === idx 
                        ? 'border-[#C6FD38] bg-[#C6FD38]/10 scale-[0.98]' 
                        : 'border-white/[0.08] bg-white/[0.03] hover:border-[#4E62F4]/50 hover:bg-[#4E62F4]/10'
                      }
                      disabled:cursor-default
                    `}
                  >
                    <div 
                      className={`
                        w-5 h-5 rounded-full border-2 transition-all flex-shrink-0 flex items-center justify-center
                        ${selectedOption === idx 
                          ? 'border-[#C6FD38] bg-[#C6FD38]' 
                          : 'border-white/30 group-hover:border-[#C6FD38]'
                        }
                      `}
                    >
                      {selectedOption === idx && (
                        <div className="w-2 h-2 bg-[#0A0B1E] rounded-full" />
                      )}
                    </div>
                    <span className={`text-sm md:text-base leading-relaxed transition-colors ${selectedOption === idx ? 'text-white' : 'text-white/80 group-hover:text-white'}`}>
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // LEAD CAPTURE SCREEN (GHL Form)
  // ═══════════════════════════════════════════════════════════════
  if (screen === 'leadCapture') {
    return (
      <div className="min-h-screen text-white relative">
        <Background />
        
        <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0A0B1E]/60 border-b border-white/5">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-center">
            <img src={LOGO_DARK} alt="FASCINAI ONE" className="h-9 md:h-10 object-contain" />
          </div>
        </header>

        <main className="relative z-10 pt-28 pb-20 px-4">
          <div className="max-w-lg mx-auto">
            {/* Success Badge */}
            <div className="text-center mb-8">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-4"
                style={{ 
                  background: `${COLORS.success}20`,
                  border: `1px solid ${COLORS.success}40`,
                  color: COLORS.success
                }}
              >
                <Zap className="w-4 h-4" />
                <span>¡Auditoría completada!</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Tu diagnóstico está listo
              </h2>
              <p className="text-white/60">
                Déjanos tus datos para enviarte el informe completo y recursos exclusivos
              </p>
            </div>

            {/* GHL Form */}
            <GlassCard className="mb-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/form/boU6dAvGvpB60QbKp0wB"
                style={{ width: '100%', height: '400px', border: 'none', borderRadius: '8px' }}
                id="inline-boU6dAvGvpB60QbKp0wB"
                data-layout="{'id':'INLINE'}"
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="AUDITOR"
                data-height="400"
                data-layout-iframe-id="inline-boU6dAvGvpB60QbKp0wB"
                data-form-id="boU6dAvGvpB60QbKp0wB"
                title="AUDITOR"
              />
            </GlassCard>

            {/* Continue Button */}
            <div className="text-center">
              <button
                onClick={handleShowFinalResult}
                className="inline-flex items-center gap-2 px-10 py-5 text-lg font-bold rounded-2xl transition-all duration-300 hover:scale-105"
                style={{ 
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.pink})`,
                  boxShadow: `0 10px 40px ${COLORS.primary}40`,
                  color: '#fff'
                }}
              >
                Ver mi diagnóstico
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // LOADING SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (screen === 'loading') {
    return (
      <div className="min-h-screen text-white flex items-center justify-center relative">
        <Background />
        
        <div className="relative z-10 text-center px-4">
          <AnimatedLoader />
          
          <h2 className="text-xl md:text-2xl font-bold mb-4">
            Analizando tu negocio...
          </h2>
          <p className="text-white/50 text-base max-w-sm mx-auto leading-relaxed">
            Calculando cuánto dinero estás dejando sobre la mesa y cómo recuperarlo
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RESULT SCREEN
  // ═══════════════════════════════════════════════════════════════
  if (screen === 'result' && result) {
    const scoreColor = getScoreColor(result.globalScore);
    const diff = result.globalScore - BENCHMARK.media;

    return (
      <div className="min-h-screen text-white relative">
        <Background />
        <Confetti active={showConfetti} />
        
        <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0A0B1E]/60 border-b border-white/5">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-center">
            <img src={LOGO_DARK} alt="FASCINAI ONE" className="h-9 md:h-10 object-contain" />
          </div>
        </header>

        <main className="relative z-10 pt-28 pb-20 px-4">
          <div className="max-w-xl mx-auto space-y-6">
            {/* Badge */}
            <div className="text-center">
              <div 
                className="inline-block px-5 py-2.5 rounded-full text-xs font-bold tracking-wider uppercase"
                style={{ 
                  background: `linear-gradient(135deg, ${COLORS.lime}25, ${COLORS.lavender}20)`,
                  border: `1px solid ${COLORS.lime}30`,
                  color: COLORS.lime
                }}
              >
                Tu diagnóstico
              </div>
            </div>

            {/* Score Box */}
            <GlassCard highlight className="text-center">
              <div className="mb-4">
                <span 
                  className="text-7xl md:text-8xl font-extrabold"
                  style={{ color: scoreColor }}
                >
                  {result.globalScore}
                </span>
                <span className="text-2xl md:text-3xl text-white/30 font-semibold">/100</span>
              </div>
              <div 
                className="text-lg font-bold mb-8"
                style={{ color: scoreColor }}
              >
                {getScoreLabel(result.globalScore)}
              </div>
              
              {/* Thermometer */}
              <div className="h-3 bg-white/10 rounded-full overflow-hidden mb-3 backdrop-blur-sm">
                <div 
                  className="h-full rounded-full transition-all duration-1000 relative overflow-hidden"
                  style={{ 
                    width: `${result.globalScore}%`,
                    background: `linear-gradient(90deg, ${COLORS.danger}, ${COLORS.warn}, ${COLORS.success})`
                  }}
                />
              </div>
              <div className="flex justify-between text-xs text-white/30 font-medium">
                <span>0 Crítico</span>
                <span>50</span>
                <span>100 Óptimo</span>
              </div>
            </GlassCard>

            {/* Money Lost */}
            <GlassCard className="text-center">
              <div className="text-sm text-white/50 uppercase tracking-wider mb-3 flex items-center justify-center gap-2">
                Dinero que estás dejando sobre la mesa
              </div>
              <div 
                className="text-4xl md:text-5xl font-extrabold mb-3"
                style={{ color: COLORS.danger }}
              >
                {formatNumber(result.moneyLost)} € / mes
              </div>
              <div className="text-sm text-white/50 leading-relaxed">
                Estimación basada en tu facturación actual y el potencial sin explotar. 
                Son <strong className="text-white">{formatNumber(result.moneyLost * 12)} € al año</strong>.
              </div>
            </GlassCard>

            {/* Category Scores */}
            <GlassCard>
              <h3 
                className="text-xs uppercase tracking-widest font-bold mb-6"
                style={{ color: COLORS.lime }}
              >
                Tu score por área
              </h3>
              <div className="space-y-5">
                {Object.keys(CATEGORIES).map(cat => {
                  const score = result.categoryScores[cat];
                  const color = getScoreColor(score);
                  return (
                    <div key={cat}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-white/70 flex items-center gap-2">
                          <span>{CATEGORY_EMOJIS[cat]}</span>
                          {CATEGORIES[cat].name}
                        </span>
                        <span className="font-bold" style={{ color }}>{score}/100</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden backdrop-blur-sm">
                        <div 
                          className="h-full rounded-full transition-all duration-1000"
                          style={{ width: `${score}%`, background: color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </GlassCard>

            {/* Critical Gaps */}
            <GlassCard>
              <h3 
                className="text-xs uppercase tracking-widest font-bold mb-6"
                style={{ color: result.gaps.length === 0 ? COLORS.success : COLORS.danger }}
              >
                {result.gaps.length === 0 ? 'Tu diagnóstico' : `${result.gaps.length === 1 ? 'Tu hueco crítico' : `Tus ${result.gaps.length} huecos críticos`}`}
              </h3>
              {result.gaps.length === 0 ? (
                <div className="flex items-start gap-4">
                  <div 
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0"
                    style={{ background: COLORS.success }}
                  >
                    ✓
                  </div>
                  <div>
                    <div className="font-bold text-white mb-1">No tienes huecos críticos</div>
                    <div className="text-sm text-white/50 leading-relaxed">
                      Tu marketing está optimizado. Tu siguiente palanca es escalar lo que ya funciona.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  {result.gaps.map((cat, idx) => (
                    <div key={cat} className="flex items-start gap-4">
                      <div 
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                        style={{ background: COLORS.danger }}
                      >
                        {idx + 1}
                      </div>
                      <div>
                        <div className="font-bold text-white mb-1">
                          {CATEGORIES[cat].name} 
                          <span className="text-white/40 font-normal ml-2">({result.categoryScores[cat]}/100)</span>
                        </div>
                        <div className="text-sm text-white/50 leading-relaxed">{GAP_FIXES[cat]}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>

            {/* Benchmark */}
            <GlassCard className="text-center">
              <h3 
                className="text-xs uppercase tracking-widest font-bold mb-6"
                style={{ color: COLORS.lime }}
              >
                Cómo estás vs tu nicho
              </h3>
              <div className="flex justify-center gap-8 md:gap-12 mb-4">
                <div>
                  <div className="text-4xl font-extrabold" style={{ color: scoreColor }}>{result.globalScore}</div>
                  <div className="text-xs text-white/30 uppercase tracking-wider mt-1">Tú</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold text-white/40">{BENCHMARK.media}</div>
                  <div className="text-xs text-white/30 uppercase tracking-wider mt-1">Media</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold" style={{ color: COLORS.lime }}>{BENCHMARK.top10}</div>
                  <div className="text-xs text-white/30 uppercase tracking-wider mt-1">Top 10%</div>
                </div>
              </div>
              <p className="text-sm text-white/50">
                {diff >= 0 
                  ? `Estás ${diff} puntos por encima de la media`
                  : `Estás ${Math.abs(diff)} puntos por debajo de la media`
                }
              </p>
            </GlassCard>

            {/* Action Plan */}
            <GlassCard
              style={{ 
                background: `linear-gradient(135deg, ${COLORS.success}08, ${COLORS.lime}05)`,
                borderColor: `${COLORS.success}20`
              }}
            >
              <h3 
                className="text-xs uppercase tracking-widest font-bold mb-6"
                style={{ color: COLORS.success }}
              >
                Tu plan para subir 30 puntos en 30 días
              </h3>
              <div className="space-y-4">
                {result.gaps.slice(0, 3).map(cat => (
                  <div key={cat} className="flex items-start gap-3">
                    <span style={{ color: COLORS.success }}>✓</span>
                    <span className="text-sm text-white/60 leading-relaxed">{GAP_FIXES[cat]}</span>
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Action Buttons - No CTA to FASCINAI */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
              <button
                onClick={handleDownloadPDF}
                data-testid="download-pdf-btn"
                className="flex items-center gap-2 px-8 py-4 rounded-full text-sm font-medium transition-all hover:scale-105"
                style={{ 
                  background: `linear-gradient(135deg, ${COLORS.lime}, #B8E843)`,
                  color: COLORS.bg
                }}
              >
                <Download className="w-4 h-4" />
                Descargar mi informe en PDF
              </button>
              <button
                onClick={handleRestart}
                data-testid="restart-audit-btn"
                className="flex items-center gap-2 px-6 py-4 border border-white/10 text-white/60 rounded-full text-sm hover:border-white/30 hover:text-white/80 transition-all"
              >
                <RotateCcw className="w-4 h-4" />
                Repetir auditoría
              </button>
            </div>

            {/* CTA Taller */}
            <GlassCard 
              className="mt-8 text-center"
              style={{ 
                background: `linear-gradient(135deg, ${COLORS.lime}10, ${COLORS.primary}08)`,
                borderColor: `${COLORS.lime}30`
              }}
            >
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                ¿Quieres cerrar estos huecos con IA?
              </h3>
              <p className="text-white/60 mb-6 max-w-md mx-auto">
                Únete al taller gratuito del 25 de Junio y aprende a crear todos los assets de tu funnel en minutos con FASCINAI ONE.
              </p>
              <a
                href="https://fascinads.com/registro-fascinai-one/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl text-base font-bold transition-all hover:scale-105"
                style={{ 
                  background: COLORS.lime,
                  color: COLORS.bg
                }}
              >
                Reservar mi plaza gratis
                <ArrowRight className="w-5 h-5" />
              </a>
            </GlassCard>
          </div>
        </main>

        <footer className="relative z-10 border-t border-white/5 py-6">
          <div className="max-w-4xl mx-auto px-4 text-center text-white/30 text-sm">
            <p>© 2024 FASCINAI ONE. Todos los derechos reservados.</p>
          </div>
        </footer>
      </div>
    );
  }

  return null;
};

export default AuditorMarketing;
